import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASSISTANT_NAME } from '../lib/assistant';
import { VagdeviMark } from './VagdeviMark';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AskSkillXzenAi } from './AskSkillXzenAi';
import { requestCoursePanel } from '../lib/ai-launcher-bus';
import { onEveryWelcomeEnd, whenWelcomeOver } from '../lib/welcome-sequence';

/**
 * The assistant, reachable from anywhere.
 *
 * The panel itself is unchanged — this is a way in, not a second
 * implementation. What it adds is the one thing the backend forces: the
 * assistant answers from one course's material, and every question passes an
 * entitlement check on a course id, so a launcher opened on the dashboard has
 * to establish which course before it can ask anything.
 *
 * Three decisions worth keeping:
 *
 *   * It offers only courses that can answer. /ai/courses returns the
 *     published courses that are both indexed and entitled, so a learner is
 *     never handed a choice that then refuses every question.
 *   * It hides rather than explains itself. No AI_ASK permission, feature
 *     switched off, or nothing indexed yet, and there is no bubble at all —
 *     a launcher whose only behaviour is an apology is worse than no
 *     launcher.
 *   * Inside the player it defers. The rail already has the assistant, so
 *     the button switches to that tab instead of opening a second thread.
 *
 * `autoOpen` is for the catalogue, where the assistant introduces itself out
 * loud and so should already be visible. It opens the panel once, when the
 * course list arrives — not on mount, because until then the launcher does
 * not know whether it has anything to offer. It fires once per mount, so a
 * visitor who puts the panel away is not handed it again on the next page.
 *
 * Not on a phone, though. At phone width the dock is a sheet over the whole
 * page (see the 640px rule in styles.css), so opening it unprompted covered
 * the course page — Enrol button included — before the visitor had read a
 * word. There the bubble is shown and the panel waits to be asked for. The
 * width is read at the moment of opening, not on mount, so a tablet turned
 * between arrival and the end of the welcome is judged as it is held.
 */

interface AiLauncherProps {
  /** Open the panel unprompted, once, as soon as there is something to ask. */
  autoOpen?: boolean;
}

interface AskableCourse {
  courseId: string;
  code: string;
  title: string;
  enrolled: boolean;
  lastAccessedAt: string | null;
}

/** Remembering the choice is most of what makes this feel like one assistant. */
const LAST_COURSE_KEY = 'sx.ai.course';

/** Below this the list is short enough to read; above it, searching is faster. */
const SEARCH_FROM = 8;

/** The width at which styles.css turns the dock into a full-width sheet. */
const SHEET_QUERY = '(max-width: 640px)';

/** Whether opening the panel now would cover the page rather than sit beside it. */
function panelWouldCoverPage(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SHEET_QUERY).matches;
}

export function AiLauncher({ autoOpen = false }: AiLauncherProps = {}) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const permitted = can('AI_ASK');

  const [courses, setCourses] = useState<AskableCourse[] | null>(null);
  /**
   * Closed, open, or minimised.
   *
   * Minimised is the one that earns its keep: the panel stays mounted and
   * out of sight, so a conversation — and an answer still being written —
   * survives being put away. Closing is the deliberate end of a thread.
   */
  const [mode, setMode] = useState<'closed' | 'open' | 'min'>('closed');
  const open = mode === 'open';
  const [courseId, setCourseId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Asked once per session, not per page: the answer is a list of courses and
  // an on/off switch, neither of which changes while someone browses.
  useEffect(() => {
    if (!permitted) return;
    let cancelled = false;
    api<{ enabled: boolean; items: AskableCourse[] }>('/ai/courses')
      .then((payload) => {
        if (cancelled) return;
        setCourses(payload.enabled ? payload.items : []);
      })
      .catch((caught: ApiError) => {
        // Nothing to say to a learner here. An assistant that cannot be
        // reached is an assistant that is not offered.
        if (!cancelled) setCourses([]);
        void caught;
      });
    return () => {
      cancelled = true;
    };
  }, [permitted]);

  // The remembered course, but only if it is still one they may ask about —
  // an entitlement can lapse between visits.
  useEffect(() => {
    if (!courses || courses.length === 0) return;
    setCourseId((current) => {
      if (current && courses.some((course) => course.courseId === current)) return current;
      let remembered: string | null = null;
      try {
        remembered = window.localStorage.getItem(LAST_COURSE_KEY);
      } catch {
        /* private mode: the choice just will not survive a reload */
      }
      if (remembered && courses.some((course) => course.courseId === remembered)) {
        return remembered;
      }
      // One course to choose from is not a choice worth presenting.
      return courses.length === 1 ? (courses[0]?.courseId ?? null) : null;
    });
  }, [courses]);

  useEffect(() => {
    if (!courseId) return;
    try {
      window.localStorage.setItem(LAST_COURSE_KEY, courseId);
    } catch {
      /* as above */
    }
  }, [courseId]);

  // Opening unprompted on the catalogue, once the spoken welcome is over.
  //
  // It waits for two things, in this order: a list of courses it can actually
  // ask about, and the assistant's portrait finishing her introduction. The
  // second is what keeps the panel from opening over the top of her.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpened.current) return;
    // Still asking which courses can answer, or there are none and there is
    // nothing to open.
    if (!permitted || courses === null || courses.length === 0) return;
    autoOpened.current = true;
    return whenWelcomeOver(() => {
      // On a phone the bubble alone is the invitation; see the note above.
      if (panelWouldCoverPage()) return;
      // Only from closed: a visitor who has already put the panel away, or is
      // mid-conversation, should not have it reopened underneath them.
      setMode((value) => (value === 'closed' ? 'open' : value));
    });
  }, [permitted, courses, autoOpen]);

  // After every later welcome too, for a visitor who comes back to the
  // catalogue with this launcher still mounted: the chat is hidden while she
  // speaks and opens as she leaves, conversation intact. On a phone it is
  // left as it was — a panel that was open reappears by itself once the
  // welcome stops hiding it, and a closed one stays a bubble.
  useEffect(() => {
    if (!autoOpen) return;
    return onEveryWelcomeEnd(() => {
      if (panelWouldCoverPage()) return;
      setMode('open');
    });
  }, [autoOpen]);

  // Escape puts the panel away rather than ending it, and focus goes back to
  // the button. Not a click-outside dismissal either: a half-read answer
  // should not vanish because someone clicked the page behind it.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setMode('min');
      buttonRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // Reserve the space the fixed bubble would otherwise cover at the foot of a
  // scrolled page. Declared from here rather than in the shell so a page that
  // has no launcher does not carry the empty strip.
  const visible = permitted && (courses?.length ?? 0) > 0;
  useEffect(() => {
    if (!visible) return;
    document.body.dataset.sxLauncher = 'on';
    return () => {
      delete document.body.dataset.sxLauncher;
    };
  }, [visible]);

  const launch = useCallback(() => {
    // The player's rail owns the assistant where there is one.
    if (requestCoursePanel()) return;
    // The button minimises rather than closes, so clicking it twice by
    // accident costs nothing. Ending a conversation is the ✕ in the header.
    setMode((value) => (value === 'open' ? 'min' : 'open'));
  }, []);

  const selected = useMemo(
    () => courses?.find((course) => course.courseId === courseId) ?? null,
    [courses, courseId],
  );

  const matches = useMemo(() => {
    if (!courses) return [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return courses;
    return courses.filter(
      (course) =>
        course.title.toLowerCase().includes(needle) ||
        course.code.toLowerCase().includes(needle),
    );
  }, [courses, filter]);

  // Nothing to launch: no permission, feature off, or no course indexed and
  // entitled. The bubble is absent rather than disabled.
  if (!visible || !courses) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`ailaunch${open ? ' ailaunch--on' : ''}`}
        onClick={launch}
        aria-expanded={open}
        aria-controls="sx-ai-dock"
        aria-label={
          open ? `Minimise ${ASSISTANT_NAME}` : mode === 'min' ? `Reopen ${ASSISTANT_NAME}` : `Ask ${ASSISTANT_NAME}`
        }
        title={
          open
            ? 'Minimise'
            : mode === 'min'
              ? `Reopen ${ASSISTANT_NAME} — your conversation is still here`
              : `Ask ${ASSISTANT_NAME} about your courses`
        }
      >
        <span className="ailaunch__icon" aria-hidden="true">
          {open ? (
            '⌄'
          ) : (
            <VagdeviMark size={34} />
          )}
        </span>
        <span className="ailaunch__label">
          <span className="ailaunch__name">{ASSISTANT_NAME}</span>
          {/* What pressing it does. The name alone says who, not what. */}
          <span className="ailaunch__hint">Ask AI</span>
        </span>
        {/* A parked conversation is otherwise indistinguishable from none. */}
        {mode === 'min' ? <span className="ailaunch__dot" aria-hidden="true" /> : null}
      </button>

      {mode !== 'closed' ? (
        <div
          id="sx-ai-dock"
          // Hidden, not unmounted. Minimising keeps the thread — and keeps a
          // streaming answer streaming — so coming back resumes rather than
          // restarts.
          className={`aidock${mode === 'min' ? ' aidock--min' : ''}`}
          role="dialog"
          aria-modal="false"
          aria-label={`${ASSISTANT_NAME} assistant`}
          aria-hidden={mode === 'min'}
          tabIndex={-1}
          ref={panelRef}
        >
          <header className="aidock__head">
            <div className="aidock__who">
              <p className="aidock__title">
                <VagdeviMark size={20} /> {ASSISTANT_NAME}
              </p>
              <p className="aidock__ctx">
                {selected ? selected.title : 'Choose a course to ask about'}
              </p>
            </div>
            <div className="aidock__acts">
              {selected && courses.length > 1 ? (
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  onClick={() => {
                    setCourseId(null);
                    setFilter('');
                  }}
                >
                  Change
                </button>
              ) : null}
              <button
                type="button"
                className="iconbtn"
                aria-label="Minimise"
                title="Minimise — keeps this conversation"
                onClick={() => {
                  setMode('min');
                  buttonRef.current?.focus();
                }}
              >
                —
              </button>
              <button
                type="button"
                className="iconbtn"
                aria-label="Close"
                title="Close — ends this conversation"
                onClick={() => {
                  setMode('closed');
                  buttonRef.current?.focus();
                }}
              >
                ✕
              </button>
            </div>
          </header>

          {selected ? (
            // Keyed on the course: switching course starts a new thread
            // rather than carrying the previous course's history into one it
            // cannot cite.
            <AskSkillXzenAi
              key={selected.courseId}
              courseId={selected.courseId}
              onOpenSource={(lessonId) => {
                // Off the player there is nothing to seek, so a citation
                // becomes navigation: open the course at that lesson.
                navigate(`/learn/${selected.courseId}?lesson=${lessonId}`);
                // Minimised, not closed: the learner is being sent to read
                // the source and will want the thread when they come back.
                setMode('min');
              }}
            />
          ) : (
            <div className="aidock__pick">
              <p className="aidock__picknote">
                Answers come from one course's material at a time, so pick the course
                you want to ask about.
              </p>

              {courses.length >= SEARCH_FROM ? (
                <input
                  className="field__input"
                  type="search"
                  value={filter}
                  placeholder="Find a course…"
                  aria-label="Find a course"
                  onChange={(event) => setFilter(event.target.value)}
                />
              ) : null}

              <ul className="aidock__list">
                {matches.map((course) => (
                  <li key={course.courseId}>
                    <button
                      type="button"
                      className="aidock__course"
                      onClick={() => setCourseId(course.courseId)}
                    >
                      <span className="aidock__coursename">{course.title}</span>
                      <span className="aidock__coursemeta">
                        {course.code}
                        {course.enrolled ? ' · enrolled' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {matches.length === 0 ? (
                <p className="muted">No course matches "{filter.trim()}".</p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
